import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";
import { resolveUrl } from "@/lib/api";

const links = [
    { to: "/", label: "Home" },
    { to: "/gallery", label: "Gallery" },
    { to: "/videos", label: "Videos" },
    { to: "/services", label: "Services" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();
    const { settings } = useSettings();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-navbar"
            className={`sticky top-0 z-40 transition-all duration-300 ${
                scrolled ? "bg-cream/95 backdrop-blur-md border-b border-stone-border" : "bg-cream"
            }`}
        >
            <div className="container-padded flex items-center justify-between h-20">
                <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group">
                    {settings?.logo_url ? (
                        <img src={resolveUrl(settings.logo_url)} alt={settings.business_name} className="w-9 h-9 object-contain" />
                    ) : (
                        <div className="w-9 h-9 bg-walnut text-cream flex items-center justify-center font-serif text-xl">J</div>
                    )}
                    <div className="leading-tight">
                        <div className="font-serif text-xl text-ink">{settings?.business_name || "Jassi Wood Works"}</div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-walnut-light">Est. Craftsmanship</div>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-9">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-sm tracking-wide transition-colors hover:text-walnut-light ${
                                    isActive ? "text-walnut font-medium" : "text-stone-muted"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <button
                    data-testid="navbar-quote-cta"
                    onClick={() => navigate("/contact#quote")}
                    className="hidden md:inline-flex bg-walnut text-cream px-5 py-2.5 text-sm tracking-wide hover:bg-walnut-dark transition-colors"
                >
                    Get a Quote
                </button>

                <button
                    data-testid="navbar-mobile-toggle"
                    onClick={() => setOpen(!open)}
                    className="md:hidden p-2 text-ink"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div data-testid="navbar-mobile-menu" className="md:hidden border-t border-stone-border bg-cream">
                    <div className="container-padded py-4 flex flex-col gap-3">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                onClick={() => setOpen(false)}
                                data-testid={`mnav-link-${l.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `py-2 text-base ${isActive ? "text-walnut font-medium" : "text-stone-muted"}`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <button
                            data-testid="mnav-quote-cta"
                            onClick={() => {
                                setOpen(false);
                                navigate("/contact#quote");
                            }}
                            className="mt-2 bg-walnut text-cream px-5 py-3 text-sm tracking-wide"
                        >
                            Get a Quote
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
}
